<template>
  <div class="ic-wrapper max-w-xl mx-auto">
    <div class="ic-container mx-auto" :style="styles">
      <div ref="cropperWrapper" style="width:100%;height:100%;"></div>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        style="display:none;"
        @change="onFileSelected"
      />
      <camera
        ref="ICCamera"
        v-if="isMobile"
        :show="isShowCamera"
        @captured="onCaptured"
        @close-camera="onCloseCamera"
        @camera-error="onCameraError"
      />
    </div>
    <loader v-if="loading" />
  </div>
</template>
<script>
import Cropper from 'cropperjs'
import Camera from './Camera.vue'
import Loader from './Loader.vue'
export default {
  name: 'CaptureImage',
  components: {
    Camera,
    Loader
  },
  provide() {
    return {
      toSave: this.toSave,
      loadImage: this.loadImage,
      loadImageFromFile: this.loadImageFromFile
    }
  },
  props: {
    width: {
      type: String,
      default: '350px'
    },
    height: {
      type: String,
      default: '250px'
    },
    complete: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cropper: null,
      imageUrl: null,
      isMobile: false,
      isShowCamera: false,
      isCropActive: false,
      isEditorMode: false,
      loading: false
    }
  },
  watch: {
    complete(value) {
      this.loading = value
    },
    imageUrl(newVal, oldVal) {
      this._destroyCropper()
      if (oldVal) {
        URL.revokeObjectURL(oldVal)
      }
      if (newVal) {
        this.$nextTick(() => {
          this._initCropper()
        })
      }
    }
  },
  computed: {
    styles() {
      const { width, height } = this
      return { width, height, position: 'relative', overflow: 'hidden' }
    }
  },
  mounted() {
    this.isMobile = /Mobi|Android/i.test(navigator.userAgent) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    this.$emit('register-event')
  },
  beforeDestroy() {
    this.isShowCamera = false
    this._destroyCropper()
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl)
    }
  },
  methods: {
    async _initCropper() {
      if (!this.$refs.cropperWrapper) return
      const img = document.createElement('img')
      img.style.cssText = 'max-width:100%;max-height:100%;display:block;'

      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = this.imageUrl
      })

      if (!this.$refs.cropperWrapper) return
      this._sourceImg = img
      this.$refs.cropperWrapper.appendChild(img)
      this.cropper = new Cropper(img)
      this.isCropActive = false

      const selection = await this.cropper.getCropperSelection()

      if (selection) {
        selection.$clear()
      }

      window['cropper'] = this.cropper
    },
    _destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      this._sourceImg = null
      if (this.$refs.cropperWrapper) {
        this.$refs.cropperWrapper.innerHTML = ''
      }
    },
    loadImage() {
      if (this.isMobile) {
        this.showCamera()
      } else {
        this.$refs.fileInput.click()
      }
    },
    loadImageFromFile() {
      this.$refs.fileInput.click()
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.imageUrl = URL.createObjectURL(file)
      this.$emit('change-image', true)
      this.isEditorMode = true
      this.changeEditorMode(true)
      event.target.value = ''
    },
    onCaptured(blob) {
      this.imageUrl = URL.createObjectURL(blob)
      this.$emit('change-image', true)
      this.isEditorMode = true
      this.changeEditorMode(true)
    },
    onCloseCamera() {
      this.isShowCamera = false
      this.isEditorMode = this.imageUrl !== null
      this.changeEditorMode(this.isEditorMode)
    },
    onCameraError() {
      this.$emit('load-error')
    },
    openWindow() {
      this.isMobile ? this.showCamera() : this.loadImage()
    },
    showCamera() {
      this.isShowCamera = true
      this.isEditorMode = false
      this.changeEditorMode(true)
    },
    rotate() {
      if (!this.cropper) return
      const cropperImage = this.cropper.getCropperImage()
      if (cropperImage) cropperImage.$rotate('90deg')
    },
    mirror() {
      if (!this.cropper) return
      const cropperImage = this.cropper.getCropperImage()
      if (!cropperImage) return
      // $getTransform() retorna la matriz [a, b, c, d, e, f]
      // a = scaleX — negativo significa que está volteado horizontalmente
      const t = cropperImage.$getTransform()
      cropperImage.$setTransform(t[0] < 0 ? 1 : -1, t[1], t[2], t[3], t[4], t[5])
    },
    async toSave() {
      if (!this.cropper) return
      this.loading = true
      try {
        const cropperCanvas = this.cropper.getCropperCanvas()
        const selection = this.cropper.getCropperSelection()

        const naturalW = this._sourceImg ? this._sourceImg.naturalWidth : 0
        const naturalH = this._sourceImg ? this._sourceImg.naturalHeight : 0
        const canvasW = cropperCanvas ? cropperCanvas.offsetWidth : 0
        const canvasH = cropperCanvas ? cropperCanvas.offsetHeight : 0

        // Abortar si no se pueden garantizar las dimensiones originales
        if (!naturalW || !naturalH || !canvasW || !canvasH) {
          this.loading = false
          this.$emit('load-error')
          return
        }

        // Px de imagen original por cada px de display (object-fit: contain)
        // Ej: imagen 3000x3000 en canvas 350x250 → scale = 3000/250 = 12
        const scale = Math.max(naturalW / canvasW, naturalH / canvasH, 1)

        const hasSelection = selection && selection.width > 0 && selection.height > 0
        let canvas

        if (hasSelection) {
          // Recorte: multiplicar px de display por scale → px originales
          canvas = await selection.$toCanvas({
            width: Math.round(selection.width * scale),
            height: Math.round(selection.height * scale)
          })
        } else {
          // Sin recorte: exportar siempre a resolución original exacta
          canvas = await cropperCanvas.$toCanvas({
            width: naturalW,
            height: naturalH
          })
        }

        if (!canvas) {
          this.loading = false
          return
        }

        canvas.toBlob(blob => {
          this.$emit('save-image', blob)
          this.loading = false
        }, 'image/png')
      } catch (e) {
        this.loading = false
      }
    },
    close() {
      this.isShowCamera = false
      this._destroyCropper()
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl)
        this.imageUrl = null
      }
      this.isCropActive = false
      this.$emit('change-image', false)
      this.changeEditorMode(false)
    },
    activeCropTool() {
      if (!this.cropper) return
      const selection = this.cropper.getCropperSelection()
      if (!selection) return
      if (this.isCropActive) {
        selection.$clear()
        this.isCropActive = false
      } else {
        const cropperCanvas = this.cropper.getCropperCanvas()
        const w = cropperCanvas ? cropperCanvas.offsetWidth : 0
        const h = cropperCanvas ? cropperCanvas.offsetHeight : 0
        selection.$change(w * 0.1, h * 0.1, w * 0.8, h * 0.8)
        this.isCropActive = true
      }
    },
    changeEditorMode(value) {
      this.$emit('change-editormode', value)
    }
  }
}
</script>
<style>
.ic-wrapper {
  padding: 1rem;
  position: relative;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.max-w-xl {
  max-width: 60rem;
}

.ic-container cropper-canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
